import React, { useState, useMemo, Fragment } from "react";
import { Autocomplete, TextField, Chip, Checkbox } from "@mui/material";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp, faMinus, faPlus, faSort } from "@fortawesome/free-solid-svg-icons";
import { FixedSizeList as List } from "react-window";
import { formatCurrency, formatNumber, formatDate, funCheckNumber } from "../Utility/utility";
import { counter } from "@fortawesome/fontawesome-svg-core";
import { format } from "date-fns";
import { setDetailParams, setNewEntryMode } from "../state/masterItemEntrySlice";
import { useDispatch } from "react-redux";


// --- Formatting Helpers ---


const formatValue = (value, type) => { return type === "date" ? formatDate(value) : type === "currency" ? formatCurrency(value) : type === "number" ? formatNumber(value) : type == "num" ? funCheckNumber(value).toFixed(2) : value }

// --- MasterGrid Component ---
const MasterGrid = ({
    columns = [],
    data = [],
    detailColumns = [],
    groupKey = null,
    showPaging = false,
    pageSize = 10,
    exportHeader = {},
    headClass = "",
    filterClass = "",
    footerClass = "",
    stockFilter = {},
    expandableRow = false,
    showDiffBal = false,
    virtualize = true,
    minusCheckCol = "",
    isExport = true

}) => {
    const [filters, setFilters] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [expandedRows, setExpandedRows] = useState({});
    const [expandAllChecked, setExpandAllChecked] = useState(false);
    const disapatch = useDispatch()
    const numberColType = ["number", "currency"]
    const formatNumberForCompare = (value) => {
        return Number(value || 0).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };
    console.log(columns)
    // --- Filtered Data ---

    const filteredData = useMemo(() => {

        let temp = data.filter(row =>
            Object.entries(filters).every(([key, selected]) => {
                if (!selected?.length) return true;

                return selected.some(selValue => {
                    const rowValue = row[key];

                    // DATE
                    const col = columns.find(c => c.accessor === key);
                    const dCol = detailColumns.find(c => c.accessor === key);

                    if (col?.type === "date" || dCol?.type === "date") {
                        const d = new Date(rowValue);
                        const formatted = isNaN(d) ? "" : format(d, "dd/MM/yyyy");
                        return formatted === selValue;
                    }

                    // NUMBER
                    if (col?.type === "number" || dCol?.type === "number") {
                        return formatNumberForCompare(rowValue) === selValue;
                    }

                    // STRING
                    return String(rowValue ?? "") === String(selValue);
                });
            })
        );

        // =========================
        // STOCK + BALANCE FILTER
        // =========================
        const balanceKey = stockFilter?.accessor || "SummaryBalancePcs";

        temp = temp.filter(row => {
            const bal = Number(row[balanceKey] || 0);

            // ---- STOCK TYPE FILTER ----
            if (stockFilter?.type === "POSITIVE" && bal <= 0) return false;
            if (stockFilter?.type === "ZERO" && bal !== 0) return false;

            // ---- INCLUDE ZERO FILTER ----
            // if (stockFilter?.includeZero === false && bal === 0) return false;

            // ---- MIN FILTER ----
            if (stockFilter?.min !== "" && bal < Number(stockFilter.min)) return false;

            // ---- MAX FILTER ----
            if (stockFilter?.max !== "" && bal > Number(stockFilter.max)) return false;

            return true;
        });

        return temp;

    }, [data, filters, columns, detailColumns, stockFilter]);
    const getUniqueValues = (col, filData) => {
        return [...new Set(
            filData
                .map(item => item[col.accessor])
                .filter(v => v !== undefined && v !== null)
                // ✅ EXCLUDE NEGATIVE VALUES
                .filter(v => {
                    if (col.excludeNegative) {
                        return Number(v) != 0;
                    }
                    return true;
                })
        )].map(v =>
            col.type === "number"
                ? formatValue(v, "number")
                : col.type === "date"
                    ? formatValue(v, "date")
                    : v
        );
    };

    // --- Sorted Data ---
    const sortedData = useMemo(() => {
        if (!sortConfig.key) return filteredData;
        const colType = columns.find(c => c.accessor === sortConfig.key)?.type;

        return [...filteredData].sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];
            if (numberColType.includes(colType)) { aVal = Number(aVal) || 0; bVal = Number(bVal) || 0; }
            else if (colType === "date") { aVal = new Date(aVal).getTime() || 0; bVal = new Date(bVal).getTime() || 0; }
            else { aVal = aVal?.toString().toLowerCase() || ""; bVal = bVal?.toString().toLowerCase() || ""; }
            if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
    }, [filteredData, sortConfig, columns, detailColumns]);

    // --- Pagination ---
    const totalPages = showPaging ? Math.ceil(sortedData.length / pageSize) : 1;
    const paginatedData = useMemo(() => {
        if (!showPaging) return sortedData;
        const start = (currentPage - 1) * pageSize;
        return sortedData.slice(start, start + pageSize);
    }, [sortedData, currentPage, pageSize, showPaging]);

    // --- Totals ---
    const totals = useMemo(() => {
        const result = {};
        columns.forEach(col => { if (numberColType.includes(col.type)) result[col.accessor] = filteredData.reduce((sum, r) => sum + (Number(r[col.accessor]) || 0), 0); });
        return result;
    }, [filteredData, columns]);


    const diff_Balance = useMemo(() => (totals?.OpBal_Dr || 0) - (totals?.OpBal_Cr || 0), [totals]);

    // --- Handlers ---
    const handleFilterChange = (key, values) => { setFilters(prev => ({ ...prev, [key]: values || [] })); setCurrentPage(1); };
    const handleSort = col => {
        if (!col.sortable) return;
        setSortConfig(prev => {
            if (prev.key !== col.accessor) return { key: col.accessor, direction: "asc" };
            if (prev.direction === "asc") return { key: col.accessor, direction: "desc" };
            if (prev.direction === "desc") return { key: null, direction: null };
            return { key: col.accessor, direction: "asc" };
        });
    };
    const toggleRow = index => setExpandedRows(prev => ({ ...prev, [index]: !prev[index] }));

    const expandAll = (expand) => {
        const newExpanded = {};
        groupedData.forEach((_, i) => { newExpanded[i] = expand; });
        setExpandedRows(newExpanded);
        setExpandAllChecked(expand);
    };

    // --- Grouped Data for Expandable Rows ---
    // const groupedData = useMemo(() => {
    //     if (!expandableRow || !groupKey) return [];
    //     const groups = {};
    //     filteredData.forEach(row => {
    //         const key = row[groupKey] || "Other";
    //         if (!groups[key]) groups[key] = { groupName: key, rows: [], totals: {} };
    //         groups[key].rows.push(row);
    //     });
    //     Object.values(groups).forEach(g => columns.forEach(col => {
    //         if (numberColType.includes(col.type)) g.totals[col.accessor] = g.rows.reduce((sum, r) => sum + (Number(r[col.accessor]) || 0), 0);
    //     }));
    //     return Object.values(groups);
    // }, [filteredData, columns, groupKey, expandableRow]);
    const handleColClick = (link, key) => {

        if (link && link != "" && link != null && key && key != "" && key != null) {
            disapatch(setDetailParams({ MItem_Code: key }))
            disapatch(setNewEntryMode(false))
            window.open(link, "_blank")
        }

    }
    const groupedData = useMemo(() => {
        if (!expandableRow || !groupKey) return [];

        const groups = {};

        filteredData.forEach(row => {
            const key = row[groupKey] || "Other";

            if (!groups[key]) {
                groups[key] = { groupName: key, rows: [], totals: {} };
            }

            groups[key].rows.push(row);
        });

        Object.values(groups).forEach(g => {

            // ✅ IMPORTANT: sort by date (or TransactionId if needed)
            // g.rows.sort(
            //     (a, b) => new Date(a.Chal_Date) - new Date(b.Chal_Date)
            // );

            columns.forEach(col => {
                if (!numberColType.includes(col.type)) return;

                const values = g.rows.map(r => Number(r[col.accessor]) || 0);

                switch (col.sumMode || "sum") {

                    // 🔢 Normal sum
                    case "sum":
                        g.totals[col.accessor] = values.reduce((a, b) => a + b, 0);
                        break;

                    // 🟢 First row value
                    case "first":
                        g.totals[col.accessor] = values[0] || 0;
                        break;

                    // 🔵 Last row value
                    case "last":
                        g.totals[col.accessor] = values[values.length - 1] || 0;
                        break;

                    // 🔥 Last NON-ZERO value (BEST for stock balance)
                    case "lastNonZero":
                        g.totals[col.accessor] =
                            [...values].reverse().find(v => v !== 0) || 0;
                        break;

                    default:
                        g.totals[col.accessor] = values.reduce((a, b) => a + b, 0);
                }
            });
        });

        return Object.values(groups);

    }, [filteredData, columns, groupKey, expandableRow]);


    // --- Column Widths ---
    const columnWidths = useMemo(() => [60, ...columns.map(col => parseInt(col.width) || 100)], [columns]);

    // --- Export Handlers ---
    const funExcelExportWithExpand = async () => {

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Report");

        const companyName = exportHeader?.companyName || "";
        const reportName = exportHeader?.reportName || "";
        const reportDate = new Date().toLocaleDateString("en-GB");

        let currentRow = 1;

        const totalColumns = Math.max(columns.length, detailColumns.length) + 1; // +1 for Serial
        const lastColumnLetter = worksheet.getColumn(totalColumns).letter;

        // ================= COMPANY NAME =================
        worksheet.mergeCells(`A${currentRow}:${lastColumnLetter}${currentRow}`);
        const companyCell = worksheet.getCell(`A${currentRow}`);
        companyCell.value = companyName;
        companyCell.font = { bold: true, size: 16 };
        companyCell.alignment = { horizontal: "center" };
        currentRow++;

        // ================= REPORT NAME =================
        worksheet.mergeCells(`A${currentRow}:${lastColumnLetter}${currentRow}`);
        const reportCell = worksheet.getCell(`A${currentRow}`);
        reportCell.value = reportName;
        reportCell.font = { bold: true, size: 13 };
        reportCell.alignment = { horizontal: "center" };
        currentRow++;

        // ================= DATE =================
        worksheet.mergeCells(`A${currentRow}:${lastColumnLetter}${currentRow}`);
        const dateCell = worksheet.getCell(`A${currentRow}`);
        dateCell.value = `Generated On: ${reportDate}`;
        dateCell.font = { size: 8 };
        dateCell.alignment = { horizontal: "right" };
        currentRow += 2;

        // ================= HEADER =================
        const headerRow = worksheet.addRow([
            "No.",
            ...columns.map(col => col.header)
        ]);

        headerRow.font = { bold: true };

        headerRow.eachCell(cell => {
            cell.alignment = { horizontal: "center" };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFDCE6F1" }
            };
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" }
            };
        });

        // ================= GROUP LOOP =================
        groupedData.forEach((group, gIndex) => {

            const isExpanded = expandedRows[gIndex];

            // -------- GROUP ROW --------
            const groupRow = worksheet.addRow([
                gIndex + 1,
                ...columns.map(col => {
                    if (col.accessor === groupKey) return group.groupName;
                    if (numberColType.includes(col.type)) return group.totals[col.accessor] || 0;
                    return "";
                })
            ]);

            groupRow.eachCell((cell, colNumber) => {
                cell.font = { bold: true };
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFF2F2F2" }
                };

                if (columns[colNumber - 2]?.type === "number") {
                    cell.numFmt = "#,##0.00";
                    cell.alignment = { horizontal: "right" };
                }

                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" }
                };
            });

            // -------- DETAIL SECTION --------
            if (isExpanded && group.rows.length > 0) {

                // Detail Header
                const detailHeader = worksheet.addRow([
                    "#",
                    ...detailColumns.map(col => col.header)
                ]);

                detailHeader.eachCell(cell => {
                    cell.font = { bold: true };
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "FFD9EAD3" }
                    };
                    cell.alignment = { horizontal: "center" };
                    cell.border = {
                        top: { style: "thin" },
                        left: { style: "thin" },
                        bottom: { style: "thin" },
                        right: { style: "thin" }
                    };
                });

                // Detail Rows
                group.rows.forEach((detail, dIndex) => {

                    const detailRow = worksheet.addRow([
                        `${gIndex + 1}.${dIndex + 1}`,
                        ...detailColumns.map(col => {
                            if (col.type === "date") {
                                return detail[col.accessor]
                                    ? new Date(detail[col.accessor]) // ✅ convert to Date
                                    : null;
                            }
                            return detail[col.accessor];
                        })
                    ]);

                    detailRow.eachCell((cell, colNumber) => {

                        const column = detailColumns[colNumber - 2];

                        if (!column) return;

                        // ✅ Number formatting
                        if (column.type === "number") {
                            cell.numFmt = "#,##0.00";
                            cell.alignment = { horizontal: "right" };
                        }

                        // ✅ Date formatting
                        if (column.type === "date") {
                            cell.numFmt = "dd/mm/yyyy"; // ✅ correct Excel format
                            cell.alignment = { horizontal: "center" };
                        }

                        // ✅ Currency (optional if you have it)
                        if (column.type === "currency") {
                            cell.numFmt = "#,##0.00";
                            cell.alignment = { horizontal: "right" };
                        }

                        // ✅ Border (keep as is)
                        cell.border = {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" }
                        };
                    });
                });

                // ===== DETAIL TOTAL ROW =====
                const detailTotalRow = worksheet.addRow([
                    "",
                    ...detailColumns.map((col, idx) => {
                        if (idx === 0) return "TOTAL";
                        if (col.type == "number") {
                            if (col?.islastValue == true)
                                return formatNumber(
                                    group.rows.reduce(
                                        (sum, r, index) => {
                                            const val = Number(r[col.accessor]) || 0;
                                            return index === 0 ? val : index == group.rows.length - 1 ? val : 0;
                                        },
                                        0
                                    )
                                )
                            else if (col?.isdifference == true)
                                return formatNumber(
                                    group.rows.reduce(
                                        (sum, r, index) => {
                                            const val = Number(r[col.accessor]) || 0;
                                            return index === 0 ? val : sum - val;
                                        },
                                        0
                                    )
                                )
                            else

                                return formatNumber(group.rows.reduce((sum, r) => sum + (Number(r[col.accessor]) || 0), 0))
                        }
                        else if (col.type === "currency")

                            return formatCurrency(group.rows.reduce((sum, r) => sum + (Number(r[col.accessor]) || 0), 0))

                        else return ""

                        // if (numberColType.includes(col.type)) {
                        //     return group.rows.reduce(
                        //         (sum, r) => sum + (Number(r[col.accessor]) || 0),
                        //         0
                        //     );
                        // }
                        // if (idx === 0) return "TOTAL";
                        return "";
                    })
                ]);

                detailTotalRow.eachCell((cell, colNumber) => {

                    cell.font = { bold: true };
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "FFE2EFDA" }
                    };

                    if (detailColumns[colNumber - 2]?.type === "number") {
                        cell.numFmt = "#,##0.00";
                        cell.alignment = { horizontal: "right" };
                    }

                    cell.border = {
                        top: { style: "thin" },
                        left: { style: "thin" },
                        bottom: { style: "thin" },
                        right: { style: "thin" }
                    };
                });

                worksheet.addRow([]);
            }
        });

        // ================= GRAND TOTAL =================
        const grandTotalRow = worksheet.addRow([
            "",
            ...columns.map((col, idx) =>
                numberColType.includes(col.type)
                    ? totals[col.accessor] || 0
                    : idx === 0 ? "GRAND TOTAL" : ""
            )
        ]);

        grandTotalRow.eachCell((cell, colNumber) => {
            cell.font = { bold: true };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFD9D9D9" }
            };

            if (columns[colNumber - 2]?.type === "number") {
                cell.numFmt = "#,##0.00";
                cell.alignment = { horizontal: "right" };
            }

            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" }
            };
        });

        // ================= DIFFERENCE ROW =================
        if (showDiffBal && diff_Balance !== 0) {

            const diffRow = worksheet.addRow([
                "",
                ...columns.map((col, idx) => {
                    if (
                        (diff_Balance < 0 && col.accessor === "OpBal_Cr") ||
                        (diff_Balance > 0 && col.accessor === "OpBal_Dr")
                    ) {
                        return Math.abs(diff_Balance);
                    }
                    if (idx === 0) return "Difference In Opening Balance";
                    return "";
                })
            ]);

            diffRow.eachCell((cell, colNumber) => {

                cell.font = { bold: true, color: { argb: "FFFF0000" } };
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFFFE5E5" }
                };

                if (columns[colNumber - 2]?.type === "number") {
                    cell.numFmt = "#,##0.00";
                    cell.alignment = { horizontal: "right" };
                }

                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" }
                };
            });
        }

        // ================= AUTO WIDTH =================
        worksheet.columns.forEach(column => {
            let maxLength = 8;
            column.eachCell({ includeEmpty: true }, cell => {
                const length = cell.value ? cell.value.toString().length : 0;
                if (length > maxLength) maxLength = length;
            });
            column.width = Math.min(maxLength + 2, 25);
        });

        // Freeze header
        worksheet.views = [{ state: "frozen", ySplit: 4 }];

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `${reportName || "Report"}.xlsx`);
    };
    const funExcelExport = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Report");

        const companyName = exportHeader?.companyName || "";
        const reportName = exportHeader?.reportName || "";
        const reportDate = new Date().toLocaleDateString("en-GB");

        const totalColumns = columns.length;

        // 🟢 Company Name Row
        worksheet.mergeCells(1, 1, 1, totalColumns);
        const companyRow = worksheet.getCell("A1");
        companyRow.value = companyName;
        companyRow.font = { size: 16, bold: true };
        companyRow.alignment = { horizontal: "center" };

        // 🟢 Report Name Row
        worksheet.mergeCells(2, 1, 2, totalColumns);
        const reportRow = worksheet.getCell("A2");
        reportRow.value = reportName;
        reportRow.font = { size: 13, bold: true };
        reportRow.alignment = { horizontal: "center" };

        // 🟢 Date Row
        worksheet.mergeCells(3, 1, 3, totalColumns);
        const dateRow = worksheet.getCell("A3");
        dateRow.value = `Generated On: ${formatDate(new Date())}`;
        dateRow.font = { size: 8 };
        dateRow.alignment = { horizontal: "right" };

        // Empty row
        // worksheet.addRow([]);

        // 🔵 Table Header Row
        const headerRow = worksheet.addRow(columns.map(col => col.header));
        headerRow.font = { bold: true };
        headerRow.alignment = { horizontal: "center" };

        headerRow.eachCell(cell => {
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };
        });

        // 🔵 Data Rows
        filteredData.forEach(row => {
            const rowData = columns.map(col => {
                if (numberColType.includes(col.type)) {
                    return Number(row[col.accessor]) || 0;
                }
                return formatValue(row[col.accessor], col?.type);
            });

            const dataRow = worksheet.addRow(rowData);

            dataRow.eachCell(cell => {
                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" },
                };
            });
        });

        // 🔴 Totals Row
        const totalRow = columns.map((col, index) => {
            if (numberColType.includes(col.type)) {
                return totals[col.accessor];
            }
            if (index === 0) return "TOTAL";
            return "";
        });

        const addedTotalRow = worksheet.addRow(totalRow);
        addedTotalRow.font = { bold: true };

        addedTotalRow.eachCell(cell => {
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };
        });

        // 📏 Auto Column Width
        worksheet.columns.forEach(column => {
            column.width = 18;
        });

        // 💾 Save File
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `${reportName}.xlsx`);
    };
    const exportToExcel = () => {
        if (expandableRow)
            funExcelExportWithExpand()
        else
            funExcelExport()
    }
    const funPDFExport = () => {
        const doc = new jsPDF("landscape");

        const companyName = exportHeader?.companyName || "";
        const reportName = exportHeader?.reportName || "";
        const reportDate = new Date().toLocaleDateString("en-GB");

        const headers = columns.map(col => col.header.toUpperCase());

        const body = filteredData.map((row, indx) => [
            `${indx + 1}`,
            ...columns.map(col =>
                formatValue(row[col.accessor], col?.type)
            )
        ]);

        // Add totals row+
        const numberTypes = new Set(numberColType);
        const totalRow = ['']; // first blank column
        columns.forEach((col, index) => {
            if (index === 0) {
                totalRow.push("TOTAL");
            } else if (numberTypes.has(col.type)) {
                totalRow.push(formatNumber(totals[col.accessor]));
            } else {
                totalRow.push("");
            }
        });

        body.push(totalRow);
        // const totalRow = columns.map((col, index) => {
        //     if (numberColType.includes(col.type)) {
        //         return formatNumber(totals[col.accessor]);
        //     }
        //     if (index === 0) return "TOTAL";
        //     return "";
        // });

        // const totalCol = [''] // add onw blank for no# Col
        // body.push([...totalCol, ...totalRow]);

        autoTable(doc, {
            head: [["No.#", ...headers]],
            body: body,
            startY: 27, // Leave space for header
            theme: "grid",
            styles: {
                fontSize: 8,
                cellPadding: 2,
            },
            headStyles: {
                fillColor: "#0891b3",
                textColor: "#FFFFFF",
                fontStyle: "bold",

            },
            didDrawPage: function (data) {
                // 🔵 Company Name
                data.settings.margin.top = 27;
                const pageWidth = doc.internal.pageSize.getWidth();
                const pageHeight = doc.internal.pageSize.getHeight();

                doc.setFontSize(16);
                doc.setFont(undefined, "bold");
                doc.text(companyName, doc.internal.pageSize.getWidth() / 2, 15, {
                    align: "center",
                });

                // 🔵 Report Name
                doc.setFontSize(12);
                doc.setFont(undefined, "bold");
                doc.text(reportName, doc.internal.pageSize.getWidth() / 2, 23, {
                    align: "center",
                });

                doc.setFontSize(9);
                doc.text(
                    `Generated on: ${reportDate}`,
                    14,
                    pageHeight - 10
                );
                // 🔴 Page Number
                const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
                const totalPages = doc.internal.getNumberOfPages();

                doc.setFontSize(9);
                doc.text(
                    `Page ${pageNumber} of ${totalPages}`,
                    pageWidth - 20,
                    pageHeight - 10
                );
            },
            didParseCell: function (data) {
                const rowIndex = data.row.index;
                const colIndex = data.column.index;

                if (colIndex > 0) {
                    const colType = columns[colIndex - 1]?.type;
                    if (numberColType.includes(colType)) {
                        data.cell.styles.halign = "right";
                    }
                }
                // Total row
                if (rowIndex === body.length - 1) {
                    data.cell.styles.fontStyle = "bold";
                    data.cell.styles.fillColor = "#0891b3";
                    data.cell.styles.textColor = [255, 255, 255];
                }
            }

        });

        doc.save(`${reportName}.pdf`);
    };
    const funPDFExportWithExpand = async () => {
        const doc = new jsPDF("l", "pt", "a4");

        const companyName = exportHeader?.companyName || "";
        const reportName = exportHeader?.reportName || "";
        const reportDate = new Date().toLocaleDateString("en-GB");

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 40;
        const headerHeight = 65;

        let startY = headerHeight;

        // ================= HEADER =================
        const addHeader = () => {
            doc.setFontSize(16);
            doc.setFont(undefined, "bold");
            doc.text(companyName, pageWidth / 2, 35, { align: "center" });

            doc.setFontSize(12);
            doc.setFont(undefined, "normal");
            doc.text(reportName, pageWidth / 2, 55, { align: "center" });
        };

        // ================= FOOTER =================
        const addFooter = (data) => {
            const str = `Page ${data.pageNumber} of ${data.pageCount} | Generated: ${reportDate}`;
            doc.setFontSize(9);
            doc.setTextColor(120);
            doc.text(str, pageWidth - margin, pageHeight - 10, { align: "right" });
        };

        // ================= COLUMN WIDTH =================
        const computeColumnWidths = (cols) => {
            const availableWidth = pageWidth - margin * 2;
            const width = availableWidth / (cols.length + 1); // +1 for serial
            const styles = {};
            styles[0] = { cellWidth: 40, halign: "center" }; // Serial column

            cols.forEach((col, idx) => {
                styles[idx + 1] = {
                    cellWidth: width,
                    halign: numberColType.includes(col.type) ? "right" : "left"
                };
            });

            return styles;
        };

        const mainColStyles = computeColumnWidths(columns);
        const detailColStyles = computeColumnWidths(detailColumns);

        // ================= MAIN LOOP =================
        groupedData.forEach((group, gIndex) => {

            const isExpanded = expandedRows[gIndex];

            // -------- GROUP ROW --------
            const groupRow = [
                gIndex + 1,
                ...columns.map(col => {
                    if (col.accessor === groupKey) return group.groupName;
                    if (numberColType.includes(col.type)) return formatNumber(group.totals[col.accessor]);

                    return "";
                })
            ];

            doc.autoTable({
                startY,
                head: gIndex === 0 ? [["No.", ...columns.map(c => c.header)]] : [],
                body: [groupRow],
                theme: "grid",
                headStyles: {
                    fillColor: [41, 128, 185],//[8, 145, 178],//,
                    textColor: [255, 255, 255],
                    fontStyle: "bold",
                    halign: "center"
                },
                bodyStyles: {
                    fontSize: 8,
                    fontStyle: isExpanded ? "bold" : "normal",
                    fillColor: isExpanded ? "#f6dac1" : "#FFFFFF",
                    textColor: "#141313"
                },
                columnStyles: mainColStyles,
                margin: { left: margin, right: margin },
                didDrawPage: (data) => {
                    addHeader();
                    addFooter(data);
                    data.settings.margin.top = headerHeight;
                },
                showHead: gIndex === 0 ? "everyPage" : "never"
            });

            startY = doc.lastAutoTable.finalY + 2;

            // -------- DETAIL TABLE --------
            if (isExpanded && group.rows.length > 0) {

                const detailBody = group.rows.map((row, dIndex) => [
                    `${gIndex + 1}.${dIndex + 1}`,
                    ...detailColumns.map(col =>
                        numberColType.includes(col.type)
                            ? formatNumber(row[col.accessor])
                            : col.type == "date" ? formatDate(row[col.accessor]) :
                                row[col.accessor]
                    )
                ]);

                doc.autoTable({
                    startY,
                    head: [["#", ...detailColumns.map(c => c.header)]],
                    body: detailBody,
                    theme: "grid",
                    headStyles: {
                        fillColor: [226, 239, 218],
                        textColor: "#3a3939",
                        fontSize: 8,
                        fontStyle: "bold",
                        halign: "center"
                    },
                    bodyStyles: { fontSize: 7 },
                    columnStyles: detailColStyles,
                    margin: { left: margin, right: margin },
                    didDrawPage: (data) => {
                        addHeader();
                        addFooter(data);
                        data.settings.margin.top = headerHeight;
                    }
                });

                startY = doc.lastAutoTable.finalY + 2;
                //   numberColType.includes(col.type)
                //                             ? formatNumber(
                //                                 group.rows.reduce((sum, r) => sum + (Number(r[col.accessor]) || 0), 0)
                //                             )
                // -------- DETAIL TOTAL --------
                const detailTotal = [
                    "",
                    ...detailColumns.map((col, idx) =>
                        col.type === "number" ?
                            col?.islastValue == true ?
                                formatNumber(
                                    group.rows.reduce(
                                        (sum, r, index) => {
                                            const val = Number(r[col.accessor]) || 0;
                                            return index === 0 ? val : index == group.rows.length - 1 ? val : 0;
                                        },
                                        0
                                    )
                                )
                                :
                                col?.isdifference == true ?
                                    formatNumber(
                                        group.rows.reduce(
                                            (sum, r, index) => {
                                                const val = Number(r[col.accessor]) || 0;
                                                return index === 0 ? val : sum - val;
                                            },
                                            0
                                        )
                                    ) :
                                    formatNumber(group.rows.reduce((sum, r) => sum + (Number(r[col.accessor]) || 0), 0)) :
                            col.type === "currency" ? formatCurrency(group.rows.reduce((sum, r) => sum + (Number(r[col.accessor]) || 0), 0))



                                : idx === 0 ? "TOTAL" : ""
                    )
                ];

                doc.autoTable({
                    startY,
                    body: [detailTotal],
                    theme: "grid",
                    bodyStyles: {
                        fontSize: 8,
                        fontStyle: "bold",
                        fillColor: [226, 239, 218]
                    },
                    columnStyles: detailColStyles,
                    margin: { left: margin, right: margin },
                    didDrawPage: (data) => {
                        addHeader();
                        addFooter(data);
                        data.settings.margin.top = headerHeight;

                    }
                });

                startY = doc.lastAutoTable.finalY + 5;
            }
        });

        // ================= GRAND TOTAL =================
        const grandTotalRow = [
            "",
            ...columns.map((col, idx) =>
                numberColType.includes(col.type)
                    ? formatNumber(totals[col.accessor])
                    : idx === 0 ? "GRAND TOTAL" : ""
            )
        ];

        doc.autoTable({
            startY,
            body: [grandTotalRow],
            theme: "grid",
            bodyStyles: {
                fontSize: 9,
                fontStyle: "bold",
                fillColor: [41, 128, 185],// [207, 204, 204]
                textColor: [255, 255, 255]
            },
            columnStyles: mainColStyles,
            margin: { left: margin, right: margin },
            didDrawPage: (data) => {
                addHeader();
                addFooter(data);
                data.settings.margin.top = headerHeight;
            }
        });

        startY = doc.lastAutoTable.finalY + 5;

        // ================= DIFFERENCE ROW =================
        if (showDiffBal && diff_Balance !== 0) {
            const diffRow = [
                "",
                ...columns.map((col, idx) => {
                    if (
                        (diff_Balance < 0 && col.accessor === "OpBal_Cr") ||
                        (diff_Balance > 0 && col.accessor === "OpBal_Dr")
                    ) {
                        return formatNumber(Math.abs(diff_Balance));
                    }
                    if (idx === 0) return "Difference In Opening Balance";
                    return "";
                })
            ];

            doc.autoTable({
                startY,
                body: [diffRow],
                theme: "grid",
                bodyStyles: {
                    fontSize: 9,
                    fontStyle: "bold",
                    fillColor: [255, 229, 229],
                    textColor: [255, 0, 0]
                },
                columnStyles: mainColStyles,
                margin: { left: margin, right: margin },
                didDrawPage: (data) => {

                    addHeader();
                    addFooter(data);
                    data.settings.margin.top = headerHeight;
                }
            });
        }

        doc.save(`${reportName || "Report"}.pdf`);
    };

    const exportToPDF = () => {
        if (expandableRow)
            funPDFExportWithExpand()
        else
            funPDFExport();
    }

    // --- Render Functions ---
    const renderFilterCell = (col, filData, isDetailFilter) => {

        if (!col.filterable) return null;
        const options = [...new Set(data.map(d => formatValue(d[col.accessor], col?.type)).filter(Boolean))];
        return (
            <Autocomplete
                multiple
                size="small"
                options={getUniqueValues(col, filData)}
                // options={[
                //     ...new Set(
                //         filData.map((d) => formatValue(d[col.accessor], col?.type)).filter(Boolean)
                //     ),
                // ]}
                value={filters[col.accessor] || []}
                onChange={(e, newValue) => handleFilterChange(col.accessor, newValue)}
                renderTags={() => null}
                slotProps={{
                    chip: {
                        size: "small",
                        sx: { height: 20, fontSize: "10px", padding: "0 4px", "& .MuiChip-label": { padding: 0 } }
                    },
                    input: { sx: { padding: "2px 0px !important", fontSize: "11px" } },
                    popper: { sx: { fontSize: "12px" } },
                    listbox: {
                        sx: {
                            fontSize: "0.6rem",
                            "& .MuiAutocomplete-option": { padding: "2px 8px", minHeight: "28px" }
                        }
                    }
                }}
                sx={{

                    "& .MuiAutocomplete-inputRoot": isDetailFilter ? { marginTop: "2px", marginBottom: "2px", padding: "0px 0px !important", minHeight: "25px", background: '#FFFFFF' } : { padding: "0px 0px !important", minHeight: "30px", background: "#FFFFFF" },
                    "& .MuiOutlinedInput-root": { padding: "0px !important", height: "10px" }
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant={isDetailFilter ? "outlined" : "outlined"}
                        placeholder="Filter..."
                        color="warning"
                        size="small"
                        inputProps={{
                            ...params.inputProps, // ⚡ make sure inputProps exist
                            autoComplete: "off"
                        }}
                        sx={{
                            width: "100%",// col?.width  || "110px",
                            "& .MuiInputBase-input::placeholder": {
                                color: "#313030",      // placeholder color
                                fontSize: "11px",
                                fontWeight: 600,     // placeholder font size
                                opacity: 1,            // IMPORTANT (default MUI reduces opacity)
                            },
                            // width: ["Pcs"].includes(col.key) ? "10px" : "100px",
                            "& .MuiInputLabel-root": { fontSize: "10px", }
                        }}
                    />
                )}
            />
        );
    };

    const enableVirtual = virtualize && !expandableRow && !showPaging;

    // --- Virtualized Row ---
    const VirtualRow = ({ index, style }) => {
        const row = filteredData[index];
        return (
            <div style={{ ...style, display: "flex" }} className="text-xs hover:bg-gray-50">
                <div style={{ width: columnWidths[0] }} className="border px-2 py-1 text-center sticky left-0 bg-white z-20">{index + 1}</div>
                {columns.map((col, i) => (
                    <div key={col.accessor} style={{ width: columnWidths[i + 1] }} className={`border p-1 ${numberColType.includes(col.type) ? "text-right" : ""}`}>
                        {formatValue(row[col.accessor], col?.type)}
                    </div>
                ))}
            </div>
        );
    };




    return (
        <div>
            {/* Filter Chips */}
            <div className="flex flex-wrap gap-2 mb-2">
                {Object.entries(filters).map(([key, values]) =>
                    values?.map((val, idx) => {
                        const col = columns.find(c => c.accessor === key);
                        return <Chip key={`${key}-${idx}`} label={`${col?.header || ""}: ${val}`} onDelete={() => handleFilterChange(key, values.filter(v => v !== val))} color="primary" size="small" />;
                    })
                )}
            </div>

            {/* Export Buttons */}
            {isExport && filteredData.length > 0 && (
                <div className="flex justify-between items-center mb-3">
                    <h2 className="text-xs font-semibold">-
                        {/* Total Records: {  filteredData.length} */}
                    </h2>

                    <div className="flex gap-2">
                        <button onClick={exportToExcel} className="px-3 py-1 bg-green-600 text-white rounded text-sm">Export Excel</button>
                        <button onClick={exportToPDF} className="px-3 py-1 bg-red-600 text-white rounded text-sm">Export PDF</button>
                    </div>

                </div>
            )}
            {/* Table Container */}
            <div className={`overflow-x-auto border border-gray-300 ${!showPaging ? 'max-h-[70vh] overflow-y-auto' : ''}`}>

                {/* Virtualized Rendering */}
                {enableVirtual ? (
                    <div style={{ width: columnWidths.reduce((a, b) => a + b, 0) }}>
                        {/* Header */}
                        <div className={`flex bg-slate-200 sticky top-0 z-30 text-xs font-semibold ${headClass}`}>
                            <div style={{ width: columnWidths[0] }} className={`border px-2 py-1 text-center sticky left-0 bg-slate-200  ${headClass} z-40`}>No. </div>
                            {columns.map((col, i) => (
                                <div key={col.accessor} style={{ width: columnWidths[i + 1] }} className="border px-2 py-1 text-center" onClick={() => handleSort(col)}>
                                    <div className="flex justify-center items-center">
                                        {col.header}
                                        {col.sortable && (
                                            <FontAwesomeIcon icon={sortConfig.key === col.accessor ? (sortConfig.direction === "asc" ? faArrowUp : faArrowDown) : faSort} style={{ fontSize: 10, marginLeft: 2 }} />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Filter Row */}
                        <div className={`flex bg-white text-xs  ${filterClass}`}>
                            <div style={{ width: columnWidths[0] }} className={`border px-1 py-1 sticky left-0 bg-white z-40 ${filterClass}`} />
                            {columns.map((col, i) => (
                                <div key={col.accessor} style={{ width: columnWidths[i + 1] }} className="border px-1 py-1">{renderFilterCell(col, data, 0)}</div>
                            ))}
                        </div>

                        <List height={500} itemCount={sortedData.length} itemSize={35} width={columnWidths.reduce((a, b) => a + b, 0)}>
                            {VirtualRow}
                        </List>

                        {/* Totals Row */}
                        <div className={`flex bg-slate-200 font-semibold text-xs ${footerClass}`}>
                            <div style={{ width: columnWidths[0] }} className="border p-2">TOTAL</div>
                            {columns.map((col, i) => (
                                <div key={col.accessor} style={{ width: columnWidths[i + 1] }} className="border p-2 text-right">
                                    {numberColType.includes(col.type) ? formatNumber(totals[col.accessor]) : ""}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    /* Normal Table Rendering */
                    <table className="w-full table-fixed text-xs border-separate border-spacing-0">
                        <thead className={`sticky top-0 z-20 bg-slate-200 font-semibold  ${headClass}`}>
                            <tr>
                                <th className={` ${headClass != "" ? headClass : "bg-slate-200"} sticky left-0 w-[50px] text-center font-semibold z-10 p-1 border `} style={{ width: "50px" }}>NO.</th>
                                {columns.map(col => (
                                    <th key={col.accessor} className={` ${col?.isSticky?" sticky left-[50px]":""} border uppercase text-xs  p-2 text-center cursor-pointer ${headClass}`}
                                        style={{ width: col.width || "100px", fontSize: "0.8rem" }} onClick={() => handleSort(col)}>
                                        <div className="flex justify-center items-center">
                                            {col.header}
                                            {col.sortable && (
                                                <FontAwesomeIcon icon={sortConfig.key === col.accessor ? (sortConfig.direction === "asc" ? faArrowUp : faArrowDown) : faSort} style={{ fontSize: 10, marginLeft: 2 }} />
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>

                            {/* Filter Row */}
                            <tr className={`sticky top-[28px] bg-white ${filterClass}`}>
                                <th className={`sticky left-0 bg-gray-50 text-center uppercase font-semibold z-10 p-1 ${filterClass}`}>
                                    {expandableRow && filteredData && filteredData.length > 0 ?
                                        <div className={`flex items-center gap-1  bg-white shadow rounded-sm  border-1 border-purple-300 ${filterClass}`}> {/* gap-1 removes extra spacing */}
                                            <Checkbox
                                                checked={expandAllChecked}
                                                onChange={(e) => {
                                                    const expand = e.target.checked;
                                                    setExpandAllChecked(expand);
                                                    expandAll(expand);
                                                }}
                                                color="secondary"
                                                size="small"
                                                sx={{
                                                    margin: 0,
                                                    padding: 0
                                                }}
                                            />
                                            <span className="text-purple-700">{expandAllChecked ? "Expand" : "Expand"}</span>
                                        </div> : ""}
                                </th>
                                {columns.map(col => (
                                    <th key={col.accessor} className={`  ${col?.isSticky?" sticky left-[50px] z-10":""} p-1 border   text-center ${filterClass||"bg-white"}`}>{renderFilterCell(col, data, 0)}</th>
                                ))}
                            </tr>
                            <tr className="bg-gray-400"><td colSpan={columns.length + 1} ></td></tr>
                        </thead>

                        <tbody>
                            {expandableRow && groupKey ? (
                                groupedData.map((group, gIndex) => {
                                    const detailTotals = {};

                                    detailColumns.forEach(dc => {
                                        if (dc.type !== "number" && dc.type !== "currency") return;

                                        if (dc.islastValue) {
                                            let first = 0;
                                            let last = 0;

                                            group.rows.forEach((r, i) => {
                                                const val = Number(r[dc.accessor]) || 0;
                                                if (i === 0) first = val;
                                                if (i === group.rows.length - 1) last = val;
                                            });

                                            detailTotals[dc.accessor] = last; // or first depending on your logic
                                        }

                                        else if (dc.isdifference) {
                                            let result = 0;

                                            group.rows.forEach((r, i) => {
                                                const val = Number(r[dc.accessor]) || 0;
                                                result = i === 0 ? val : result - val;
                                            });

                                            detailTotals[dc.accessor] = result;
                                        }

                                        else {
                                            let sum = 0;

                                            group.rows.forEach(r => {
                                                sum += Number(r[dc.accessor]) || 0;
                                            });

                                            detailTotals[dc.accessor] = sum;
                                        }
                                    });
                                    return (
                                        <Fragment key={gIndex}>
                                            {/* Group Row */}
                                            <tr className="bg-slate-50 font-semibold hover:bg-gray-200">
                                                <td className="sticky left-0 bg-slate-50 z-10 p-1 border">
                                                    <div className="flex justify-between items-center px-2">{gIndex + 1}
                                                        <button onClick={() => toggleRow(gIndex)} className="bg-white text-blue-700">
                                                            <FontAwesomeIcon icon={expandedRows[gIndex] ? faMinus : faPlus} />
                                                        </button>
                                                    </div>
                                                </td>
                                                {columns.map(col => (
                                                    <td key={col.accessor} className="p-1 border bg-slate-50 font-semibold" align={col.align || "left"}>
                                                        {col.accessor === groupKey ? group.groupName : (numberColType.includes(col.type) ? formatNumber(group.totals[col.accessor]) : "")}
                                                    </td>
                                                ))}
                                            </tr>

                                            {/* Detail Rows */}
                                            {expandedRows[gIndex] && (
                                                <tr>
                                                    <td colSpan={columns.length + 1} className="p-2 border bg-yellow-50">
                                                        <div className="max-h-[250px] overflow-auto border">
                                                            <table className="w-full border-collapse bg-white text-xs">
                                                                <thead className="sticky top-0  z-10">
                                                                    <tr className="bg-gray-100">
                                                                        <th className="border  p-1 w-[60px] text-center">#</th>
                                                                        {detailColumns.map(dc => <th key={dc.accessor} style={{ fontSize: "0.7rem" }} className="border uppercase text-xs  p-1 text-center">
                                                                            {dc.header}
                                                                            {dc.sortable && (
                                                                                <FontAwesomeIcon onClick={() => handleSort(dc)} icon={sortConfig.key === dc.accessor ? (sortConfig.direction === "asc" ? faArrowUp : faArrowDown) : faSort} style={{ fontSize: 10, marginLeft: 2 }} />
                                                                            )}
                                                                        </th>)}
                                                                    </tr>
                                                                    <tr className="bg-white" style={{ background: "#fcf8f3" }}>
                                                                        <th></th>
                                                                        {detailColumns.map(dc => <th key={dc.accessor} style={{ fontSize: "0.7rem" }} className="border uppercase text-xs   text-center">

                                                                            {renderFilterCell(dc, group.rows, 1)}</th>)}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {group.rows.map((row, rIdx) => (
                                                                        <tr key={rIdx} className="hover:bg-yellow-50">
                                                                            <td className="border p-1 text-center">{`${gIndex + 1}.${rIdx + 1}`}</td>
                                                                            {detailColumns.map(dc => <td key={dc.accessor} className="border p-1" align={dc.align || "left"}>{formatValue(row[dc.accessor], dc?.type)}</td>)}
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                                <tfoot className="sticky bottom-0 bg-gray-100 font-semibold">
                                                                    <tr>
                                                                        <td className="border p-1 text-center">TOTAL</td>
                                                                        {detailColumns.map(dc => (
                                                                            <td key={dc.accessor} className="border p-1 text-right">
                                                                                {dc.type === "number"
                                                                                    ? formatNumber(detailTotals[dc.accessor] || 0)
                                                                                    : dc.type === "currency"
                                                                                        ? formatCurrency(detailTotals[dc.accessor] || 0)
                                                                                        : ""}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                    {/* <tr>
                                                                        <td className="border p-1 text-center">TOTAL</td>
                                                                        {detailColumns.map(dc => <td key={dc.accessor} className="border p-1 text-right">
                                                                            {dc.type === "number" ?
                                                                                dc?.islastValue == true ?
                                                                                    formatNumber(
                                                                                        group.rows.reduce(
                                                                                            (sum, r, index) => {
                                                                                                const val = Number(r[dc.accessor]) || 0;
                                                                                                return index === 0 ? val : index == group.rows.length - 1 ? val : 0;
                                                                                            },
                                                                                            0
                                                                                        )
                                                                                    )
                                                                                    :
                                                                                    dc?.isdifference == true ?
                                                                                        formatNumber(
                                                                                            group.rows.reduce(
                                                                                                (sum, r, index) => {
                                                                                                    const val = Number(r[dc.accessor]) || 0;
                                                                                                    return index === 0 ? val : sum - val;
                                                                                                },
                                                                                                0
                                                                                            )
                                                                                        ) :
                                                                                        formatNumber(group.rows.reduce((sum, r) => sum + (Number(r[dc.accessor]) || 0), 0)) :
                                                                                dc.type === "currency" ? formatCurrency(group.rows.reduce((sum, r) => sum + (Number(r[dc.accessor]) || 0), 0))

                                                                                    : ""

                                                                            }
                                                                        </td>)}
                                                                    </tr> */}
                                                                </tfoot>
                                                            </table>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    )
                                })
                            ) : (
                                paginatedData.map((row, idx) => {

                                    const rowIndex = (currentPage - 1) * pageSize + idx;
                                    return <tr key={rowIndex} className={`hover:bg-gray-50 border  ${minusCheckCol && minusCheckCol != "" && minusCheckCol != null ? row[minusCheckCol] < 0 ?
                                        " bg-red-100 text-red-600 font-semibold" : row[minusCheckCol] > 0 ? " bg-green-50 text-green-700 font-semibold" : "font-semibold" : ""}`}>
                                        <td className={`sticky left-0 w-[50px] bg-gray-50 text-center z-10 p-1 text-slate-700  border ${filterClass} `}>{rowIndex + 1}</td>
                                        {columns.map(col => <td key={col.accessor} className={` ${col?.isSticky?" sticky left-[50px] z-10 bg-white ":""} p-1 border ${col?.textcolor || ''}
                                        `} align={col.align || "left"}
                                        ><div onClick={() => handleColClick(col?.link, row[col?.linkaccessor])} className={`${col?.link ? 'cursor-pointer' : ""}`} >{formatValue(row[col.accessor], col?.type)}</div></td>)}
                                        </tr>  
                                        //</tbody>col?.isSticky ? <td
                                        //  key={col.accessor} className={` sticky left-[50px] bg-white  z-10 p-1  border ${col?.textcolor || ''}
                                        // `} align={col.align || "left"}
                                        // ><div onClick={() => handleColClick(col?.link, row[col?.linkaccessor])} className={`${col?.link ? 'cursor-pointer' : ""}`} >{formatValue(row[col.accessor], col?.type)}</div></td>
                                         
                                         
                                   // </tr>;
                                })
                            )}
                        </tbody>

                        {/* Totals & Diff Balance */}
                        <tfoot className={` sticky bottom-0 z-20 font-semibold  ${footerClass != "" ? footerClass : "bg-slate-200"}`}>
                            <tr className="">
                                <td className={` sticky left-0 w-[50px] ${footerClass != "" ? footerClass : "bg-slate-200"} z-20 text-center`} >TOTAL</td>
                                 {columns.map(col => <td key={col.accessor} className={` ${col?.isSticky?" sticky left-[50px] ":""}  ${footerClass != "" ? footerClass : "bg-slate-200" }border p-2 ${numberColType.includes(col.type) ? "text-right" : ""}`}>{numberColType.includes(col.type) ? formatValue(totals[col.accessor],col?.type) : ""}</td>)} 
                                 {/*  col?.isSticky ?<td      key={col.accessor} className={` sticky left-[50px] ${footerClass != "" ? footerClass : "bg-slate-200"} z-20 p-1  border }
                                        `} 
                                        >-</td>
                                        :*/}
                                       
                            </tr>
                            {showDiffBal && filteredData.length > 0 && (
                                <tr className="bg-red-100 text-red-500 uppercase">
                                    <td colSpan={2}> Difference In Opening Balance</td>
                                    {columns.map((col, idx) =>
                                        (diff_Balance < 0 && col.accessor === "OpBal_Cr") || (diff_Balance > 0 && col.accessor === "OpBal_Dr") ? (
                                            <td key={col.accessor} className="border p-2 text-right font-bold">{formatNumber(Math.abs(diff_Balance))}</td>
                                        ) : idx > 0 ? <td key={col.accessor} className="border p-2"></td> : ""
                                    )}
                                </tr>
                            )}
                        </tfoot>
                    </table>
                )}
            </div>

            {/* Pagination */}
            {showPaging && filteredData && filteredData.length > 0 && (
                <div className="flex items-center justify-between bg-white border border-gray-200 rounded-md px-4 py-2 shadow-sm mb-3">

                    {/* Left Side - Page Info */}
                    <div className="text-sm text-gray-600 font-medium">
                        Page <span className="text-indigo-600 font-semibold">{currentPage}</span>
                        {" "}of{" "}
                        <span className="text-indigo-600 font-semibold">{totalPages}</span>
                    </div>

                    {/* Right Side - Buttons */}
                    <div className="flex items-center gap-2">

                        {/* Prev Button */}
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(prev => prev - 1)}
                            className={`px-3 py-1.5 text-sm rounded-md border transition-all duration-200 
                    ${currentPage === 1
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-white text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 border-gray-300"
                                }`}
                        >
                            ← Prev
                        </button>

                        {/* Page Numbers */}
                        {[...Array(totalPages)].map((_, index) => {
                            const page = index + 1;

                            return (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`w-8 h-8 text-sm rounded-md transition-all duration-200
                                     ${currentPage === page
                                            ? "bg-indigo-600 text-white shadow-md"
                                            : "bg-white border border-gray-300 text-gray-700 hover:bg-indigo-50"
                                        }`}
                                >
                                    {page}
                                </button>
                            );
                        })}

                        {/* Next Button */}
                        <button
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            className={`px-3 py-1.5 text-sm rounded-md border transition-all duration-200 
                            ${currentPage === totalPages
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-white text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 border-gray-300"
                                }`}
                        >
                            Next →
                        </button>
                    </div>
                </div>
            )}


        </div>
    );
};

export default MasterGrid;
